/// <amd-module name="Reporting/VelocityReport/VelocityReportUserSettings" />
import { AggregationSettings } from "Reporting/BoardsCommon/Pickers/AggregationPicker";
import { IVssPageContext } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { VelocitySettings } from "Widgets/VelocityWidget/VelocityWidgetComponents/VelocitySettings";

/* Contract for the user preferences stored in the platform settings service*/
export interface VelocityReportUserSettingsData {
    aggregationSettings: AggregationSettings;
    numberOfIterations: number;
    showResolvedItemsAsCompleted: boolean;
}

/* Encapsulate state and behavior for the user preferences stored in and retrieved from the platform settings service*/
export class VelocityReportUserSettings {
    public static aggregationKey = "AggregationSettings";
    public static iterationKey = "NumberOfIterations";
    public static teamScopeName = "team";
    public static showResolvedItemsAsCompletedKey = "ShowResolvedItemsAsCompleted";

    public static async reset(pageContext: IVssPageContext, teamId: string, backlogCategory: string): Promise<void> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        const reportKey = getVelocityReportSettingsStorageKey();
        await settingsService.removeEntries(reportKey, SettingsUserScope.Me, VelocityReportUserSettings.teamScopeName, teamId);
    }

    public static async save(pageContext: IVssPageContext, newSettings: VelocitySettings, teamId: string, backlogCategory: string): Promise<void> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        const reportKey = getVelocityReportSettingsStorageKey();
        const entryPrefix = `${reportKey}/${backlogCategory}`;

        const userSettings: {
            [key: string]: any;
        } = {};
        userSettings[entryPrefix + "/" + VelocityReportUserSettings.aggregationKey] = newSettings.aggregation;
        userSettings[entryPrefix + "/" + VelocityReportUserSettings.iterationKey] = newSettings.numberOfIterations;
        userSettings[entryPrefix + "/" + VelocityReportUserSettings.showResolvedItemsAsCompletedKey] = newSettings.showResolvedItemsAsCompleted;
        await settingsService.setEntries(userSettings, SettingsUserScope.Me, VelocityReportUserSettings.teamScopeName, teamId);
    }

    public static async load(pageContext: IVssPageContext, teamId: string, backlogCategory: string): Promise<VelocityReportUserSettingsData> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");

        const userSettings: {
            [key: string]: any;
        } = await settingsService.getEntriesAsync(
            getVelocityReportSettingsStorageKey() + "/" + backlogCategory,
            SettingsUserScope.Me,
            VelocityReportUserSettings.teamScopeName,
            teamId
        );

        return {
            aggregationSettings: userSettings[VelocityReportUserSettings.aggregationKey],
            numberOfIterations: userSettings[VelocityReportUserSettings.iterationKey],
            showResolvedItemsAsCompleted: userSettings[VelocityReportUserSettings.showResolvedItemsAsCompletedKey]
        } as VelocityReportUserSettingsData;
    }
}

export function getVelocityReportSettingsStorageKey(): string {
    return "VelocityReport";
}
